import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

export default function OtpVerificationPage() {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN);
  const [statusMessage, setStatusMessage] = useState('');
  const inputRefs = useRef([]);
  const { verifySmsOtp, resendOtp, currentUser, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminOtp = location.state?.adminOtp === true;

  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [resendTimer]);

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  useEffect(() => {
    if (!currentUser) navigate('/login', { replace: true });
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!isAdminOtp && userProfile?.phoneVerified === true && userProfile?.status === 'active') {
      navigate('/', { replace: true });
    }
  }, [userProfile, navigate, isAdminOtp]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
    if (newOtp.every((d) => d !== '') && value) handleVerify(newOtp.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (pasted.length === OTP_LENGTH) {
      const newOtp = pasted.split('');
      setOtp(newOtp);
      inputRefs.current[OTP_LENGTH - 1]?.focus();
      handleVerify(pasted);
    }
  };

  const handleVerify = async (code) => {
    if (loading) return;
    setLoading(true);
    setStatusMessage('Verifying...');
    try {
      console.log('Verifying OTP:', code);
      const verified = await verifySmsOtp(code);
      console.log('Verification result:', verified);

      if (verified) {
        setStatusMessage('Phone verified! Redirecting...');
        toast.success('Phone verified successfully!');
        if (refreshProfile) await refreshProfile();
        setTimeout(() => {
          if (isAdminOtp) {
            navigate('/admin', { replace: true });
          } else {
            navigate('/set-pin', { replace: true });
          }
        }, 500);
      } else {
        setStatusMessage('Invalid code');
        toast.error('Invalid code. Please try again.');
        setOtp(Array(OTP_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      const message = err?.message || 'Verification failed.';
      setStatusMessage('Error: ' + message);
      toast.error(message);
      setOtp(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setStatusMessage('Sending new code...');
    try {
      await resendOtp();
      toast.success('New code sent!');
      setStatusMessage('New code sent — check your phone');
      setResendTimer(RESEND_COOLDOWN);
      setOtp(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err) {
      const message = err?.message || 'Failed to resend code.';
      setStatusMessage('Resend failed: ' + message);
      toast.error(message);
    }
  };

  // Determine status message colour class based on content
  const isSuccessMsg = statusMessage.includes('verified') || statusMessage.includes('Redirecting');
  const isErrorMsg = statusMessage.includes('Error') || statusMessage.includes('Invalid') || statusMessage.includes('failed');
  const statusClasses = isSuccessMsg
    ? 'text-brand-primary bg-brand-primary/10'
    : isErrorMsg
    ? 'text-error bg-error/10'
    : 'text-body bg-body/10';

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream px-5 py-8 md:px-6 md:py-12">
      <div className="w-full max-w-md text-center">
        <div className="text-5xl mb-6">📱</div>
        <h1 className="font-serif text-3xl md:text-4xl text-brand-dark mb-3 tracking-tight">
          Verify your phone
        </h1>
        <p className="text-body text-base leading-relaxed mb-10">
          We've sent a {OTP_LENGTH}-digit code to your mobile number. Enter it below to complete verification.
        </p>

        <div
          className="flex gap-2 sm:gap-3 justify-center mb-8"
          onPaste={handlePaste}
        >
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={loading}
              autoComplete="one-time-code"
              className={`w-11 h-14 sm:w-13 sm:h-16 rounded-xl border-2 text-xl sm:text-2xl font-bold text-center text-brand-dark outline-none transition-colors ${
                digit
                  ? 'border-brand-primary bg-brand-primary/[0.04]'
                  : 'border-slate-200 bg-white'
              }`}
            />
          ))}
        </div>

        {statusMessage && (
          <div className={`px-4 py-2.5 rounded-xl mb-5 text-sm font-semibold ${statusClasses}`}>
            {statusMessage}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="spinner" />
            <span className="text-body">Verifying...</span>
          </div>
        )}

        <div className="mb-6">
          {resendTimer > 0 ? (
            <span className="text-muted text-sm">Resend code in {resendTimer}s</span>
          ) : (
            <button
              onClick={handleResend}
              className="text-brand-primary font-semibold text-base bg-transparent border-none cursor-pointer py-2 px-4"
            >
              Resend Code
            </button>
          )}
        </div>

        <p className="text-muted text-xs leading-relaxed">
          Code expires in 5 minutes. You can request up to 5 codes per hour.
        </p>
      </div>
    </div>
  );
}